import { useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import { getApiUrl } from "../../../../config/api";
import {
  formatUsdWithCode,
} from "../../../../components/usageBilling/usageFormat";

import LimitAmountField, { useLimitAmount } from "../../../../components/usageBilling/LimitAmountField";

// Spend per group for the selected cycle, and each group's per-user limit.
// The limit is per user, not a budget the group shares. Users count under
// their current group; those with none, or whose group was deleted, come back
// from the backend under group_id null ("No group").
export default function GroupSpendTable({
  groups,
  isCurrent,
  canEdit,
  currentUser,
  onSaved,
  onFilterGroup,
  language,
  formatMoney = formatUsdWithCode,
  currency = "USD",
  rateState,
}) {
  const zh = language === "zh";
  const [editing, setEditing] = useState(null);
  const maxSpend = Math.max(...groups.map((g) => g.cycle_used_usd), 0);
  const th =
    "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider";
  const td = "px-4 py-3 text-sm text-gray-700 whitespace-nowrap";

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {zh ? "各群組花費" : "Spend per group"}
        </h3>
        <p className="text-sm text-gray-500">
          {zh
            ? "群組額度是每位成員各自的每月額度，不是群組共用的預算。使用者依其目前所屬群組計算。"
            : "A group's limit is what each member gets for themselves per 28-day cycle, not a budget they share. Users count under their current group."}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={th}>{zh ? "群組" : "Group"}</th>
              <th className={th}>
                {isCurrent
                  ? zh
                    ? "每人每月額度"
                    : "Limit per user"
                  : zh
                    ? "每人每月額度（目前）"
                    : "Limit per user (current)"}
              </th>
              <th className={th}>{zh ? "有使用的人數" : "Users with usage"}</th>
              <th className={th}>{zh ? "本週期花費" : "Cycle spend"}</th>
              {isCurrent && (
                <>
                  <th className={th}>{zh ? "本週花費" : "This week"}</th>
                  <th className={th}>{zh ? "超過本週額度" : "Over weekly quota"}</th>
                </>
              )}
              {canEdit && <th className={th} />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {groups.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-10 text-center text-sm text-gray-500"
                >
                  {zh ? "還沒有任何群組。" : "There are no groups yet."}
                </td>
              </tr>
            )}
            {groups.map((group) => (
              <tr key={group.group_id ?? "none"} className="hover:bg-gray-50">
                <td className={td}>
                  {/* "__none__" is the users list's filter for this row. */}
                  <button
                    type="button"
                    onClick={() => onFilterGroup(group.group_id ?? "__none__")}
                    className={`font-medium hover:text-orange-600 hover:underline ${
                      group.group_id ? "text-gray-900" : "text-gray-500"
                    }`}
                    title={
                      group.group_id
                        ? zh
                          ? "顯示這個群組的使用者"
                          : "Show this group's users"
                        : zh
                          ? "顯示沒有群組（或所屬群組已被刪除）的使用者"
                          : "Show users with no group, or whose group was deleted"
                    }
                  >
                    {group.group_id ? group.name : zh ? "無群組" : "No group"}
                  </button>
                </td>
                <td className={td}>
                  {!group.group_id ? (
                    <span className="text-gray-400">—</span>
                  ) : group.monthly_limit_per_user_usd === null ? (
                    <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800">
                      {zh ? "未設定額度" : "No limit"}
                    </span>
                  ) : (
                    formatMoney(group.monthly_limit_per_user_usd)
                  )}
                </td>
                <td className={`${td} tabular-nums`}>{group.users}</td>
                <td className={td}>
                  <div className="flex items-center gap-2">
                    <span className="min-w-[7rem] tabular-nums">
                      {formatMoney(group.cycle_used_usd)}
                    </span>
                    <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-500 rounded-full"
                        style={{
                          width: `${maxSpend > 0 ? (group.cycle_used_usd / maxSpend) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </td>
                {isCurrent && (
                  <>
                    <td className={`${td} tabular-nums`}>
                      {formatMoney(group.week_used_usd)}
                    </td>
                    <td className={`${td} tabular-nums`}>
                      {group.over_quota_users > 0 ? (
                        <span className="font-semibold text-red-600">
                          {group.over_quota_users}
                        </span>
                      ) : (
                        group.over_quota_users
                      )}
                    </td>
                  </>
                )}
                {canEdit && (
                  <td className={`${td} text-right`}>
                    {group.group_id && (
                      <button
                        type="button"
                        onClick={() => setEditing(group)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-orange-600 border border-orange-200 hover:bg-orange-50"
                      >
                        <FiEdit2 className="w-3.5 h-3.5" aria-hidden="true" />
                        {zh ? "編輯額度" : "Edit limit"}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <GroupLimitModal
          group={editing}
          initialCurrency={currency}
          rateState={rateState}
          currentUser={currentUser}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onSaved();
          }}
          language={language}
        />
      )}
    </div>
  );
}

// Set or remove one group's per-user monthly limit. Members' quotas change
// immediately; members with their own override keep it.
function GroupLimitModal({
  group, currentUser, onClose, onSaved, language, initialCurrency, rateState,
}) {
  const zh = language === "zh";
  const current = group.monthly_limit_per_user_usd;
  const draft = useLimitAmount({ initialUsd: current, initialCurrency, rateState });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const save = async (limit) => {
    setSaving(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/v2/usage-billing/admin/groups/${encodeURIComponent(group.group_id)}/limit`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ monthly_usage_limit_usd: limit }),
        },
      );
      // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      onSaved();
    } catch (err) {
      console.error("Failed to save group usage limit:", err);
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={saving ? undefined : onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="group-limit-title"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <h3 id="group-limit-title" className="text-lg font-bold text-gray-900">
            {zh ? `${group.name} 的每月額度` : `Monthly limit for ${group.name}`}
          </h3>
          <p className="text-sm text-gray-500">
            {zh
              ? "每位成員每 28 天的週期可用的金額，每週可用四分之一。"
              : "What each member gets per 28-day cycle; a quarter of it each week."}
          </p>
        </div>

        <form
          className="p-6 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.canSave && !saving) save(draft.usdLimit);
          }}
        >
          <LimitAmountField
            id="group-limit"
            label={zh ? "每人每月額度" : "Limit per user"}
            draft={draft}
            rateState={rateState}
            saving={saving}
            language={language}
            autoFocus
          />
          {draft.isZero && (
            <p className="text-xs text-amber-700">
              {zh
                ? "0 表示限制開啟後，所有沒有個人額度的成員都無法使用。"
                : "0 blocks every member without their own override once enforcement is on."}
            </p>
          )}
          <p className="text-xs text-gray-500">
            {zh
              ? "立即生效。有個人額度的成員維持原本的個人額度。"
              : "Applies immediately. Members with their own override keep it."}
          </p>
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-between gap-3 pt-3">
            {current !== null ? (
              confirmRemove ? (
                <span className="flex items-center gap-2 text-xs text-gray-600">
                  {zh ? "沒有個人額度的成員將不受限制。" : "Members without an override get no limit."}
                  <button
                    type="button"
                    onClick={() => save(null)}
                    disabled={saving}
                    className="font-medium text-red-600 hover:underline disabled:opacity-50"
                  >
                    {zh ? "移除" : "Remove"}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmRemove(true)}
                  disabled={saving}
                  className="text-sm font-medium text-red-600 hover:underline disabled:opacity-50"
                >
                  {zh ? "移除額度" : "Remove limit"}
                </button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
              >
                {zh ? "取消" : "Cancel"}
              </button>
              <button
                type="submit"
                disabled={!draft.canSave || saving}
                className="px-5 py-2 rounded-xl text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white shadow-sm disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {saving ? (zh ? "儲存中..." : "Saving...") : zh ? "儲存" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
